/**
 * The vocabulary the pure core and the Worker share. Pure — no network, no clock, no
 * globals. Every shape here mirrors a row in `migrations/0001_create_collab.sql`, in the
 * camelCase the TypeScript side reads; the mapping happens once, in `worker/`.
 */

/** The only two roles there are. Mirrors `CHECK(role IN ('admin','member'))`. */
export type Role = 'admin' | 'member';

/** Mirrors `CHECK(status IN ('pending','ready'))`. A `pending` row has no object yet. */
export type MaterialStatus = 'pending' | 'ready';

/**
 * One row of `group_members`. `leftAt` is NULL for an active member — departure is an
 * UPDATE, never a DELETE, so "Former member" is a positive fact the thread can render and
 * a re-invitation can clear (R18, and the custody Key Decision).
 */
export interface Membership {
  groupId: string;
  clerkUserId: string;
  role: Role;
  emailNormalized: string;
  joinedAt: string;
  /** `read_floor_at` (KTD3). Null on rows an older Worker wrote; `joinedAt` stands in. */
  readFloorAt: string | null;
  leftAt: string | null;
}

/**
 * The caller, as the Worker resolved them from a verified Clerk token.
 *
 * `emailVerified` is optional on purpose, and every consumer must treat absent as
 * unverified (KTD14). A JWT template that stops emitting the claim must not silently
 * become a template that verifies everyone.
 */
export interface Identity {
  clerkUserId: string;
  email?: string | null;
  emailVerified?: boolean;
}

/**
 * The one way an address becomes comparable, used by the invitation match and by the
 * roster column alike, so `A@school.org` and `a@school.org` are one person.
 *
 * It lives here rather than in either caller because a second implementation is a second
 * chance to get it wrong, and the thing it decides is who gets into a group.
 */
export function normalizeEmail(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}
